import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Button,
  Badge,
} from "reactstrap";
import { withTranslation } from "react-i18next";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { acceptReservation, getReservation, refuseReservation } from "helpers/requests";
import { useParams, useNavigate } from "react-router-dom";
import swal from "sweetalert";

const parseApplicants = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  try { return JSON.parse(data); } catch { return []; }
};

const typeBadge = (type) => {
  if (type === "ADULT") return <Badge color="primary" pill>Adulte</Badge>;
  if (type === "CHILD") return <Badge color="warning" pill>Enfant</Badge>;
  return <Badge color="success" pill>Bébé</Badge>;
};

const statusConfig = {
  PENDING: { color: "warning", label: "En attente" },
  ACCEPTED: { color: "success", label: "Acceptée" },
  REFUSED: { color: "danger", label: "Refusée" },
};

const fmtDate = (d) => {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
};

const fmtDateTime = (d) => {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const InfoItem = ({ label, value, badge }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 12, color: "#74788d", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>{label}</div>
    {badge ? (
      <div>{value}</div>
    ) : (
      <div style={{ fontSize: 15, fontWeight: 500, color: "#343a40" }}>{value || "-"}</div>
    )}
  </div>
);

const ReservationVoyage = (props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPassenger, setSelectedPassenger] = useState(null);

  useEffect(() => {
    document.title = "Reservation Voyage";
    const fetchReservation = async () => {
      try {
        const response = await getReservation(id);
        setReservation(response);
      } catch (error) {
        console.error("Error fetching reservation:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchReservation();
  }, [id]);

  const handleAccept = () => {
    swal({
      title: "Confirmer l'acceptation",
      text: "Voulez-vous accepter cette réservation ?",
      icon: "warning",
      buttons: ["Annuler", "Accepter"],
      dangerMode: false,
    }).then((willAccept) => {
      if (willAccept) {
        acceptReservation(id, reservation.user)
          .then(() => {
            swal("Réservation acceptée !", { icon: "success" }).then(() => {
              navigate("/voyage");
            });
          })
          .catch((error) => {
            console.error("Error accepting reservation:", error);
            swal("Erreur", "Impossible d'accepter la réservation", "error");
          });
      }
    });
  };

  const handleDecline = () => {
    swal({
      title: "Confirmer le refus",
      text: "Voulez-vous refuser cette réservation ?",
      icon: "warning",
      buttons: ["Annuler", "Refuser"],
      dangerMode: true,
    }).then((willDecline) => {
      if (willDecline) {
        refuseReservation(id, reservation.user)
          .then(() => {
            swal("Réservation refusée.", { icon: "error" }).then(() => {
              navigate("/voyage");
            });
          })
          .catch((error) => {
            console.error("Error declining reservation:", error);
            swal("Erreur", "Impossible de refuser la réservation", "error");
          });
      }
    });
  };

  if (isLoading) {
    return (
      <div className="page-content">
        <Container fluid className="d-flex justify-content-center align-items-center" style={{ height: "60vh" }}>
          <Spinner color="primary" />
        </Container>
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="page-content">
        <Container fluid className="text-center" style={{ paddingTop: 80 }}>
          <h4>Réservation introuvable</h4>
        </Container>
      </div>
    );
  }

  const rawApplicants = parseApplicants(reservation.applicants);
  const applicants = rawApplicants.length > 0 ? rawApplicants : [{
    type: "ADULT",
    firstname: reservation.nom || "-",
    lastname: reservation.surnom || "-",
    nationality: reservation.nationality || "-",
    documentType: "passport",
    documentNumber: reservation.n_passport || "-",
    birthday: "-",
    gender: "-",
  }];

  const sc = statusConfig[reservation.status] || statusConfig.PENDING;
  const totalPassengers = (reservation.n_adult || 0) + (reservation.n_enfant || 0) + (reservation.n_bebe || 0);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={props.t("Voyages")} breadcrumbItem={props.t("Détails de la réservation")} />

          {/* Status + Amount header */}
          <Card style={{ borderLeft: `4px solid ${sc.color === "warning" ? "#f1b44c" : sc.color === "success" ? "#34c38f" : "#f46a6a"}`, marginBottom: 20 }}>
            <CardBody className="d-flex align-items-center justify-content-between flex-wrap" style={{ padding: "16px 24px" }}>
              <div className="d-flex align-items-center gap-3">
                <Badge color={sc.color} pill style={{ fontSize: 13, padding: "6px 16px" }}>
                  {sc.label}
                </Badge>
                <span style={{ color: "#74788d", fontSize: 13 }}>
                  Demande du {fmtDateTime(reservation.createdAt)}
                </span>
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#343a40" }}>
                {parseFloat(reservation.totalAmount).toLocaleString("fr-FR")} DA
              </div>
            </CardBody>
          </Card>

          <Row>
            {/* Left column — Reservation info */}
            <Col lg={8}>
              {/* Trip details */}
              <Card className="mb-3">
                <CardBody>
                  <h5 className="mb-3" style={{ fontWeight: 600, fontSize: 16 }}>
                    <i className="bx bx-map-pin me-2" style={{ color: "#556ee6" }}></i>
                    Détails du voyage
                  </h5>
                  <Row>
                    <Col md={6}>
                      <InfoItem label="Type de voyage" value={reservation.type || "-"} />
                    </Col>
                    <Col md={6}>
                      <InfoItem label="Total passagers" value={totalPassengers} />
                    </Col>
                    <Col md={6}>
                      <InfoItem label="Date de départ" value={fmtDate(reservation.checkin_date)} />
                    </Col>
                    <Col md={6}>
                      <InfoItem label="Date de retour" value={fmtDate(reservation.checkout_date)} />
                    </Col>
                    <Col md={4}>
                      <InfoItem label="Adultes" value={reservation.n_adult || 0} />
                    </Col>
                    <Col md={4}>
                      <InfoItem label="Enfants" value={reservation.n_enfant || 0} />
                    </Col>
                    <Col md={4}>
                      <InfoItem label="Bébés" value={reservation.n_bebe || 0} />
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* Booker info */}
              <Card className="mb-3">
                <CardBody>
                  <h5 className="mb-3" style={{ fontWeight: 600, fontSize: 16 }}>
                    <i className="bx bx-user me-2" style={{ color: "#556ee6" }}></i>
                    Informations du réservant
                  </h5>
                  <Row>
                    <Col md={6}>
                      <InfoItem label="Nom" value={reservation.nom} />
                    </Col>
                    <Col md={6}>
                      <InfoItem label="Prénom" value={reservation.surnom} />
                    </Col>
                    <Col md={6}>
                      <InfoItem label="Email" value={reservation.email} />
                    </Col>
                    <Col md={6}>
                      <InfoItem label="Nationalité" value={reservation.nationality} />
                    </Col>
                    <Col md={6}>
                      <InfoItem label="N° Passeport" value={reservation.n_passport} />
                    </Col>
                    <Col md={6}>
                      <InfoItem label="Adresse" value={reservation.street} />
                    </Col>
                    <Col md={6}>
                      <InfoItem label="Code postal" value={reservation.code_postal} />
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* Passengers */}
              <Card className="mb-3">
                <CardBody>
                  <h5 className="mb-3" style={{ fontWeight: 600, fontSize: 16 }}>
                    <i className="bx bx-group me-2" style={{ color: "#556ee6" }}></i>
                    Passagers ({applicants.length})
                  </h5>
                  <div className="d-flex flex-column gap-2">
                    {applicants.map((a, i) => (
                      <div
                        key={i}
                        onClick={() => setSelectedPassenger(selectedPassenger === i ? null : i)}
                        style={{
                          border: `1px solid ${selectedPassenger === i ? "#556ee6" : "#eff2f7"}`,
                          borderRadius: 8,
                          cursor: "pointer",
                          transition: "all 0.15s",
                          background: selectedPassenger === i ? "#f8f8ff" : "#fff",
                        }}
                      >
                        {/* Summary row */}
                        <div className="d-flex align-items-center justify-content-between" style={{ padding: "12px 16px" }}>
                          <div className="d-flex align-items-center gap-3">
                            <div style={{
                              width: 36, height: 36, borderRadius: "50%",
                              background: a.type === "ADULT" ? "#556ee6" : a.type === "CHILD" ? "#f1b44c" : "#34c38f",
                              color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 13, fontWeight: 600, flexShrink: 0
                            }}>
                              {(a.firstname?.[0] || "?").toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 14 }}>
                                {a.firstname || "-"} {a.lastname || "-"}
                              </div>
                              <div style={{ fontSize: 12, color: "#74788d" }}>
                                {a.nationality || "-"}
                              </div>
                            </div>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            {typeBadge(a.type)}
                            <i className={`bx bx-chevron-${selectedPassenger === i ? "up" : "down"}`} style={{ fontSize: 18, color: "#74788d" }}></i>
                          </div>
                        </div>

                        {/* Detail card */}
                        {selectedPassenger === i && (
                          <div style={{ padding: "0 16px 16px", borderTop: "1px solid #eff2f7" }}>
                            <Row className="mt-3">
                              <Col xs={6}>
                                <InfoItem label="Prénom" value={a.firstname} />
                              </Col>
                              <Col xs={6}>
                                <InfoItem label="Nom" value={a.lastname} />
                              </Col>
                              <Col xs={6}>
                                <InfoItem label="Date de naissance" value={a.birthday || "-"} />
                              </Col>
                              <Col xs={6}>
                                <InfoItem label="Genre" value={a.gender || "-"} />
                              </Col>
                              <Col xs={6}>
                                <InfoItem label="Type de document" value={a.documentType || "-"} />
                              </Col>
                              <Col xs={6}>
                                <InfoItem label="N° Document" value={a.documentNumber || "-"} />
                              </Col>
                              <Col xs={6}>
                                <InfoItem label="Nationalité" value={a.nationality || "-"} />
                              </Col>
                            </Row>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* Right column — Account + Actions */}
            <Col lg={4}>
              {/* Client account */}
              {reservation.user && (
                <Card className="mb-3">
                  <CardBody>
                    <h5 className="mb-3" style={{ fontWeight: 600, fontSize: 16 }}>
                      <i className="bx bx-id-card me-2" style={{ color: "#556ee6" }}></i>
                      Compte client
                    </h5>
                    <div className="d-flex align-items-center mb-3">
                      <div className="avatar-sm me-3" style={{
                        width: 42, height: 42, borderRadius: "50%",
                        background: "#556ee6", color: "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 16, fontWeight: 600
                      }}>
                        {(reservation.user.firstname?.[0] || "").toUpperCase()}{(reservation.user.lastname?.[0] || "").toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 15 }}>
                          {reservation.user.firstname} {reservation.user.lastname}
                        </div>
                        <div style={{ color: "#74788d", fontSize: 13 }}>
                          {reservation.user.email}
                        </div>
                      </div>
                    </div>
                    {reservation.user.phone && (
                      <InfoItem label="Téléphone" value={reservation.user.phone} />
                    )}
                  </CardBody>
                </Card>
              )}

              {/* Payment info */}
              <Card className="mb-3">
                <CardBody>
                  <h5 className="mb-3" style={{ fontWeight: 600, fontSize: 16 }}>
                    <i className="bx bx-money me-2" style={{ color: "#556ee6" }}></i>
                    Paiement
                  </h5>
                  <InfoItem label="Montant total" value={
                    <span style={{ fontSize: 20, fontWeight: 700, color: "#343a40" }}>
                      {parseFloat(reservation.totalAmount).toLocaleString("fr-FR")} DA
                    </span>
                  } />
                  <InfoItem label="Statut de paiement" badge value={
                    <Badge color={reservation.ispayed ? "success" : "warning"} pill style={{ fontSize: 12 }}>
                      {reservation.ispayed ? "Payé" : "Non payé"}
                    </Badge>
                  } />
                  <InfoItem label="Conditions acceptées" badge value={
                    <Badge color={reservation.acceptedTerms ? "success" : "secondary"} pill style={{ fontSize: 12 }}>
                      {reservation.acceptedTerms ? "Oui" : "Non"}
                    </Badge>
                  } />
                </CardBody>
              </Card>

              {/* Actions */}
              {reservation.status === "PENDING" && (
                <Card>
                  <CardBody>
                    <h5 className="mb-3" style={{ fontWeight: 600, fontSize: 16 }}>
                      <i className="bx bx-check-shield me-2" style={{ color: "#556ee6" }}></i>
                      Actions
                    </h5>
                    <div className="d-grid gap-2">
                      <Button color="success" onClick={handleAccept} style={{ fontWeight: 600 }}>
                        <i className="bx bx-check me-1"></i> Accepter la réservation
                      </Button>
                      <Button color="danger" outline onClick={handleDecline} style={{ fontWeight: 600 }}>
                        <i className="bx bx-x me-1"></i> Refuser la réservation
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              )}
            </Col>
          </Row>
        </Container>
      </div>

    </React.Fragment>
  );
};

ReservationVoyage.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation()(ReservationVoyage);
